import { useState, useEffect } from 'react';
import { Navbar, Nav, Container, NavDropdown } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { FaRegClock, FaUserShield } from 'react-icons/fa'; // Menambahkan import FaUserShield
import authService from '../services/authService';
import KemenkeuLogo from '../assets/images/kemenkeu.png';

const NavigationBar = () => {
  const [currentUser, setCurrentUser] = useState(undefined);
  const [tokenTimeLeft, setTokenTimeLeft] = useState(null);
  const location = useLocation();

  const handleLogout = () => {
    authService.logout();
    setCurrentUser(undefined);
    window.location.href = '/';
  };

  // format milliseconds -> "Xd Yh Zm Ss"
  const formatTime = (ms) => {
    if (ms <= 0) return 'Expired';

    const totalSeconds = Math.floor(ms / 1000);

    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const pad = (n) => n.toString().padStart(2, '0');

    return `${days}d ${hours}h ${minutes}m ${pad(seconds)}s`;
  };

  useEffect(() => {
    const user = authService.getCurrentUser();

    if (user && user.token) {
      setCurrentUser(user);

      let decoded;
      try {
        decoded = jwtDecode(user.token);
      } catch (err) {
        console.error('Failed to decode token:', err);
        setTokenTimeLeft('Invalid');
        return;
      }

      // Some JWT libraries store exp in seconds (typical),
      // some systems may (incorrectly) store it as ms.
      // Detect and normalise to milliseconds.
      let expRaw = decoded.exp;
      if (!expRaw) {
        // fallback: try other common claim names
        expRaw = decoded.expiraton || decoded.expiration || decoded.expiredAt || decoded.exp_time;
      }

      if (!expRaw) {
        console.warn('Token has no exp claim');
        setTokenTimeLeft('Unknown');
        return;
      }

      let expMs;
      // If exp looks like seconds (<= 10^10), multiply by 1000.
      // If looks like milliseconds (> 10^12), keep as-is.
      if (typeof expRaw === 'number') {
        if (expRaw > 1e12) {
          expMs = expRaw; // already ms
        } else {
          expMs = expRaw * 1000; // seconds -> ms
        }
      } else if (typeof expRaw === 'string') {
        const parsed = parseInt(expRaw, 10);
        if (!isNaN(parsed)) {
          if (parsed > 1e12) expMs = parsed;
          else expMs = parsed * 1000;
        } else {
          console.warn('exp claim is not numeric:', expRaw);
          setTokenTimeLeft('Invalid');
          return;
        }
      } else {
        console.warn('Unknown exp claim type:', typeof expRaw);
        setTokenTimeLeft('Invalid');
        return;
      }

      const updateTimer = () => {
        const now = Date.now();
        const diff = expMs - now;

        if (diff <= 0) {
          setTokenTimeLeft('Expired');
          // optional: auto logout when token expired
          handleLogout();
          return;
        }

        setTokenTimeLeft(formatTime(diff));
      };

      updateTimer();
      const interval = setInterval(updateTimer, 1000);
      return () => clearInterval(interval);
    } else {
      // no user
      setTokenTimeLeft(null);
      setCurrentUser(undefined);
    }
  }, []);

  return (
    <Navbar className="navbar-beacukai" variant="dark" expand="lg">
      <Container>
        <Navbar.Brand as={Link} to="/" className="d-flex align-items-center">
          <img
            src={KemenkeuLogo}
            alt="Kementerian Keuangan RI"
            style={{ height: '40px', marginRight: '10px' }}
          />
          <div style={{ marginRight: '15px' }}>
            <span style={{ color: 'white', fontSize: '12px', fontWeight: 'bold', display: 'block' }}>
              KEMENTRIAN KEUANGAN
            </span>
            <span style={{ color: 'white', fontSize: '11px', display: 'block' }}>
              Republik Indonesia
            </span>
          </div>
        </Navbar.Brand>

        <Navbar.Toggle aria-controls="basic-navbar-nav" />
        <Navbar.Collapse id="basic-navbar-nav">
          {location.pathname === '/' && (
            <div className="navbar-marquee-container d-none d-lg-flex">
              <span className="marquee-label">Informasi &gt;</span>
              <div className="marquee-wrapper">
                <span className="marquee-content">
                  Ini adalah portal web sistem pengaduan masyarakat berbasis web
                </span>
              </div>
            </div>
          )}

          <Nav className="ms-auto align-items-center">
            {currentUser ? (
              <>
                <Nav.Link as={Link} to="/">
                  Beranda
                </Nav.Link>
                <Nav.Link as={Link} to="/berita">
                  Berita
                </Nav.Link>
                <Nav.Link as={Link} to="/pengumuman">
                  Pengumuman
                </Nav.Link>
                <Nav.Link as={Link} to="/kegiatan">
                  Kegiatan
                </Nav.Link>

                <Nav.Link
                  as={Link}
                  to={
                    currentUser.role === 'ADMIN' || currentUser.role === 'ROLE_ADMIN'
                      ? '/admin/dashboard'
                      : '/dashboard'
                  }
                >
                  Dashboard
                </Nav.Link>

                <NavDropdown title={currentUser.username} id="basic-nav-dropdown">
                  <NavDropdown.Item onClick={handleLogout}>Logout</NavDropdown.Item>
                </NavDropdown>
              </>
            ) : (
              <>
                <Nav.Link as={Link} to="/">
                  Beranda
                </Nav.Link>
                <Nav.Link as={Link} to="/berita">
                  Berita
                </Nav.Link>
                <Nav.Link as={Link} to="/pengumuman">
                  Pengumuman
                </Nav.Link>
                <Nav.Link as={Link} to="/kegiatan">
                  Kegiatan
                </Nav.Link>
                
                {/* Tombol Login Admin/Petugas diletakkan di paling kanan jika belum login */}
                <Nav.Link 
                  as={Link} 
                  to="/login" 
                  className="d-flex align-items-center ms-lg-3 border-start ps-lg-3 border-secondary"
                  title="Masuk sebagai Petugas / Admin"
                >
                  <FaUserShield size={20} className="text-white" opacity="0.8" />
                </Nav.Link>
              </>
            )}

            {/* token countdown — positioned di paling ujung kanan */}
            {currentUser && tokenTimeLeft && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginLeft: '20px',
                  color: '#FFD700',
                  fontSize: '13px',
                  fontWeight: 'normal',
                  whiteSpace: 'nowrap',
                }}
                title={`Token akan habis pada: ${tokenTimeLeft}`}
              >
                <FaRegClock size={18} style={{ marginRight: '6px' }} />
                <span>{tokenTimeLeft}</span>
              </div>
            )}
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};

export default NavigationBar;